import { createSelector } from 'reselect';
import findWhere from 'selectors/findWhere.js';

const devices = state => state.devices.models;
const equipments = state => state.equipments.models;

export const selectRootDevices = siteId => createSelector(
    [devices],
    devices => Object.values(devices).filter(device => device.site_id === siteId && device.parent_id === null)
);

// The API decides how a device is verified (VerificationProfile), served on the
// equipment; the device only picks the by_ti_type variant. null means not verifiable.
const deviceVerification = (device, equipment) => {
    const profile = equipment?.verification_profile;

    if (!profile) {
        return null;
    }

    if (!profile.by_ti_type) {
        return profile;
    }

    const variables = profile.by_ti_type[device?.extra?.ti_type];

    return variables ? { ...profile, ...variables } : null;
};

// How this device is verified (its profile), resolved against its own equipment.
export const selectVerificationProfile = deviceId => createSelector(
    [devices, equipments],
    (devices, equipments) => deviceVerification(devices[deviceId], equipments[devices[deviceId]?.equipment_id])
);

// Verifiable devices — the ones the verification status tables are about. The API answers
// it (VerificationProfile); a device without a profile has nothing to report.
export const selectVerifiableDevices = siteId => createSelector(
    [devices, equipments],
    (devices, equipments) => Object.values(devices).filter(
        device => device.site_id === siteId && deviceVerification(device, equipments[device.equipment_id])
    )
);

export const selectVerifiableDevicesByWiseBox = wiseBoxId => createSelector(
    [devices, equipments],
    (devices, equipments) => Object.values(devices).filter(
        device => device.root_device_id === wiseBoxId && deviceVerification(device, equipments[device.equipment_id])
    )
);

const deviceIds = state => state.devices.ids;

export const selectChildDevices = deviceId => createSelector(
    [deviceIds, devices],
    (ids, models) => findWhere(ids.map(id => models[id]), { parent_id: deviceId })
);

export const selectEligibleNewParents = deviceId => createSelector(
    [devices],
    models => {
        const source = models[deviceId];
        if (!source) {
            return [];
        }
        return Object.values(models).filter(d =>
            d.id !== deviceId &&
            d.site_id === source.site_id &&
            d.out_protocol_id === source.out_protocol_id
        );
    }
);
